import PropTypes from 'prop-types';
import React from 'react';
import Resetter from 'mixins/resetter';
import PhaseType from 'types/phase';
import StyledSelect from 'components/StyledSelect';
import DatePicker from 'components/DatePicker';
import TextArea from 'components/TextArea';
import moment from 'moment';
import { addFlagToPhase, clearValidation } from 'actionCreators/phase';
import requestFlagOptions from 'actionCreators/flags';
import { Button } from 'react-toolbox';
import { connect } from 'react-redux';
import ComposeWindow from 'components/ComposeWindow';
import { error } from '../../../../../../utils';
import { getPhaseErrors, getFlagOptions, getPosition } from '../../../../../../reducers';
import { DATE_FORMAT,
         DISPLAY_DATE,
         RAILS_DATE_TIME,
         NON_LHI_SOURCES } from 'config/constants';
import S from 'components/ComposeWindow/styles.scss';

const hasDueTime = flag => flag.due_time !== undefined && flag.due_time !== null;

const convertDateForSaving = (date, dueChanged) => {
  const dueDate = moment(date, DATE_FORMAT);
  if (dueChanged) {
    return dueDate.set({ hours: moment().hour(), minutes: moment().minutes() })
    .format(RAILS_DATE_TIME);
  }
  return dueDate.format(RAILS_DATE_TIME);
};

class AddFlagModal extends Resetter(React.Component) {
  static DEFAULT_STATE = {
    flag: { name: '', suboptions: [] },
    suboption: '',
    dueAt: null,
  };

  componentWillReceiveProps = newProps => (newProps.active && !this.props.active ?
    this.props.requestFlagOptions() : null);

  filterNoRepeatFlags = (list = []) => {
    const { events } = this.props.phase;
    const noRepeatList = ['Welcome Call']; // Flags we don't want to show if prev added
    const prevAddedFlags = []; // programatically generated
    for (let i = 0; i < events.length; i += 1) {
      if (noRepeatList.length === 0) break; // Breaks out of loop if noRepeatList is empty
      if (events[i].kind === 'flag_added' && noRepeatList.includes(events[i].data.name)) {
        const noRepeatInd = noRepeatList.indexOf(events[i].data.name);
        prevAddedFlags.push(events[i].data.name); // adds to be removed from list to prevAddedFlags
        noRepeatList.splice(noRepeatInd, 1); // removes flag name from noRepeatList
      }
    }
    return list.filter(flag => !prevAddedFlags.includes(flag.name));
  };

  mappedFlagOptionsToLabelsValues = (list = []) => {
    const { position, phase: { type, source }, employableType } = this.props;
    let newList = list.length === 0 ? list : this.filterNoRepeatFlags(list);
    if (source === 'IFI' && employableType === 'Installer') {
      newList = newList.filter(obj => obj.name === 'Installer Inquiry');
    }
    return newList.filter(obj => obj.selectable)
      .filter(obj => obj.name !== 'Estimator Inquiry' || position !== 'Customer Care Specialist')
      .filter(obj => obj.name !== 'Revisit' || type === 'Assessment')
      .filter(obj => obj.name !== 'Ready to Schedule')
      .filter(obj => obj.name !== 'Installer Inquiry' || (source === 'IFI' && employableType === 'Installer'))
      .filter(obj => obj.name !== 'Out For Delivery' || source !== 'IFI')
      .filter(obj => obj.name !== 'IME' || source !== 'IFI')
      .filter(obj => obj.name !== 'Extended ETA' || source !== 'IFI')
      .filter(obj => obj.name !== 'Express Job' || source !== 'IFI')
      .filter(obj => obj.name !== 'Commercial' || source !== 'IFI')
      .filter(obj => obj.name !== 'Internal Refigure' || source !== 'IFI')
      .map(obj => ({
        value: obj.name,
        label: obj.name,
        suboptions: obj.suboptions.map(value => ({ value, label: value })),
        dueAt: hasDueTime(obj)
          ? moment().add(obj.due_time, 'seconds').format(DATE_FORMAT)
          : obj.due_time,
      }));
  }

  handleFlagChange = (flagName, options) => {
    const flag = options.find(obj => obj.label === flagName);
    if (flag) {
      const suboption = flag.suboptions.length ? flag.suboptions[0].value : '';
      const dueAt = flag.dueAt;
      this.setState({
        flag: {
          name: flagName,
          suboptions: flag.suboptions,
          dueAt,
        },
        suboption,
        dueAt,
      });
    } else this.setState(this.constructor.DEFAULT_STATE);
  }

  handleSuboptionChange = suboption => this.setState({ suboption });

  handleDueChange = dueAt => this.setState({ dueAt, dueChanged: true });

  handleSubmit = () => {
    const phaseID = this.props.phase.id;
    const { flag, suboption, dueAt, dueChanged } = this.state;
    const note = this.textAreaComponentRef.textAreaRef.value;
    const flagProps = {
      name: flag.name,
      note,
      suboption,
      due_at: dueAt ? convertDateForSaving(dueAt, dueChanged) : null,
    };

    this.props.addFlagToPhase(phaseID, flagProps).then(() => {
      this.handleClose();
    })
     .catch(() => {}); // catching this to avoid console warnings...
  }

  handleCancel = () => {
    this.props.clearValidation();
    this.handleClose();
  }

  render = () => {
    const { flag, suboption, dueAt } = this.state;
    const { active, errors, flags, phase: { source } } = this.props;
    // To revert Lowe's Services Lead flag:
    // const flagOptions = this.mappedFlagOptionsToLabelsValues(flags); <- restore this
    // Remove the following two assignments: mappedFlagOptions and flagOptions
    const { phase: { type, store: { region_name } } } = this.props;
    const flagOptions = this.mappedFlagOptionsToLabelsValues(flags).reduce((acc, curr) => {
      const flagName = 'Lowe\'s Services Lead';
      if ((!NON_LHI_SOURCES.includes(source) &&
           curr.value === flagName &&
           region_name === '02' &&
           type === 'Assessment') ||
           curr.value !== flagName) acc.push(curr);
      return acc;
    }, []);
    return (<ComposeWindow
      active={active}
      title="Add Flag"
      onClose={this.handleCancel}
    >
      <StyledSelect
        label="Flag Name"
        className={S.select}
        options={flagOptions}
        onChange={flagName => this.handleFlagChange(flagName, flagOptions)}
        value={flag.name}
        required
        placeholder="Select a flag"
        error={error('name', errors)}
      />
      {flag.suboptions.length > 0 && <StyledSelect
        label="Suboptions"
        className={S.select}
        options={flag.suboptions}
        onChange={this.handleSuboptionChange}
        value={suboption}
        required
        error={error('suboption', errors)}
      />}
      {flag.dueAt && <DatePicker
        label="Due Date"
        className={S.select}
        onChange={this.handleDueChange}
        value={moment(dueAt, DATE_FORMAT).format(DISPLAY_DATE)}
        error={error('due_at', errors)}
        required
      />}
      <TextArea
        defaultValue=""
        label="Notes *"
        errors={errors.note}
        ref={(comp) => { this.textAreaComponentRef = comp; }}
      />
      <div className={S.actions}>
        <Button
          label="Add Flag"
          primary flat ripple
          className={S.submit}
          onClick={this.handleSubmit}
        />
      </div>
    </ComposeWindow>);
  }
}

AddFlagModal.propTypes = {
  active: PropTypes.bool.isRequired,
  addFlagToPhase: PropTypes.func.isRequired,
  clearValidation: PropTypes.func.isRequired,
  requestFlagOptions: PropTypes.func.isRequired,
  phase: PhaseType.isRequired,
  errors: PropTypes.object,
  flags: PropTypes.array,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.string.isRequired,
  employableType: PropTypes.string.isRequired,
};

export default connect(
  state => ({
    position: getPosition(state),
    errors: getPhaseErrors(state),
    flags: getFlagOptions(state),
    employableType: state.user.employable_type,
  }),
  { addFlagToPhase, clearValidation, requestFlagOptions },
)(AddFlagModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/addFlagModal.jsx